import AssessmentIcon from "@mui/icons-material/Assessment";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import WavesIcon from "@mui/icons-material/Waves";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DetailOdbernehoMista from "../components/DetailOdbernehoMista";
import Vodomery from "../components/Vodomery";
import { compareHouseAsc, compareHouseDesc } from "../utils/addressUtil";
import { handleApiError } from "../utils/errorHandler";
import {
  fetchAllOdbernaMista,
  fetchOdberneMisto,
} from "./../services/odbernaMistaService";

export default function OdbernaMistaPage() {
  const [collectionPoints, setCollectionPoints] = useState([]);
  const [selectedCollectionPoint, setSelectedCollectionPoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkMode, setBulkMode] = useState(false);
  const [allMeters, setAllMeters] = useState([]);
  const showOdberatelNazev =
    allMeters?.some((m) => m.odberatelNazev != null) ||
    collectionPoints?.some((cp) => cp.odberatelNazev != null);
  const [obce, setObce] = useState([]);
  const [selectedObec, setSelectedObec] = useState("");
  const [radioFilter, setRadioFilter] = useState("vse");
  const [sortOption, setSortOption] = useState("");
  // all = Vše; vodomery, pausaly, srazkovaVoda
  const [filterState, setFilterState] = useState({
    all: true,
    vodomery: false,
    pausaly: false,
    srazkovaVoda: false,
  });

  // pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const mapAllMetersFromCollectionPoints = (odbernaMista) =>
    odbernaMista.flatMap((cp) =>
      (cp.vodomery || []).map((m) => ({
        ...m,
        cisloOm: cp.cisloOm,
        odberatelNazev: cp.odberatelNazev,
        cisloObce: cp.cisloObce,
        ulice: cp.ulice,
        obec: cp.obec,
        adresa: `Číslo obce: ${cp.cisloObce}, ${cp.ulice}, ${cp.obec}`,
        periodaOdectu: cp.periodaOdectu,
        dnes: cp.dnes,
        zobrazitVyberTypu: cp.zobrazitVyberTypu,
      }))
    );

  const refreshCollectionPointsData = async () => {
    const result = await fetchAllOdbernaMista();
    if (!result.success) {
      setError(handleApiError(result));
      return;
    }

    const odbernaMista = result.data;
    setCollectionPoints(odbernaMista);
    setAllMeters(mapAllMetersFromCollectionPoints(odbernaMista));
    setObce(Array.from(new Set(odbernaMista.map((cp) => cp.cisloObce))));

    if (selectedCollectionPoint?.cisloOm) {
      const detailResult = await fetchOdberneMisto(selectedCollectionPoint.cisloOm);
      if (detailResult.success) {
        setSelectedCollectionPoint(detailResult.data);
      }
    }
  };

  useEffect(() => {
    setSearchTerm("");
    setPage(0);
  }, [selectedObec]);

  useEffect(() => {
    const getOdbernaMista = async () => {
      setError("");
      const result = await fetchAllOdbernaMista();
      if (result.success) {
        const odbernaMista = result.data;
        setCollectionPoints(odbernaMista);
        setAllMeters(mapAllMetersFromCollectionPoints(odbernaMista));
        const uniqueObce = Array.from(
          new Set(odbernaMista.map((cp) => cp.cisloObce))
        );
        setObce(uniqueObce);
      } else {
        setError(handleApiError(result));
      }
      setLoading(false);
    };

    getOdbernaMista();
  }, []);

  const handleRadioChange = (event) => {
    setRadioFilter(event.target.value);
  };

  const handleViewDetail = async (cpNumber) => {
    setLoading(true);
    setError("");
    const result = await fetchOdberneMisto(cpNumber);
    if (result.success) {
      setSelectedCollectionPoint(result.data);
    } else {
      setError(handleApiError(result));
    }
    setLoading(false);
  };

  const handleBack = () => {
    setSelectedCollectionPoint(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredPoints = useMemo(() => {
    return collectionPoints.filter((cp) => {
      const odberatelNazev = (cp.odberatelNazev || "").trim();
      const hay =
        `${cp.cisloOm} ${cp.cisloObce} ${cp.ulice} ${cp.obec} ${showOdberatelNazev ? odberatelNazev : ""}`.toLowerCase();
      if (!hay.includes(searchTerm.toLowerCase())) return false;
      if (selectedObec && String(cp.cisloObce) !== String(selectedObec))
        return false;
      if (!filterState.all) {
        if (filterState.vodomery && !(cp.vodomery?.length > 0)) return false;
        if (filterState.pausaly && !(cp.pausaly?.length > 0)) return false;
        if (filterState.srazkovaVoda && !(cp.srazkovaVoda?.length > 0))
          return false;
      }
      return true;
    });
  }, [collectionPoints, searchTerm, selectedObec, filterState, showOdberatelNazev]);

  const filteredMeters = useMemo(() => {
    return allMeters
      .filter(
        (m) =>
          (!selectedObec || String(m.cisloObce) === String(selectedObec)) &&
          (radioFilter === "vse" ||
            (radioFilter === "nezadane" && (m.muzeDnesZadat || !m.jizZadano)) ||
            (radioFilter === "zadane" && (!m.muzeDnesZadat || m.jizZadano)))
      )
      .filter((m) =>
        `${m.cisloOm} ${m.cisloVodomeru} ${m.adresa}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
  }, [allMeters, selectedObec, radioFilter, searchTerm]);

  // Změna stavu checkboxů
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    // Pokud uživatel zaškrtnul "Vše", ostatní se vypnou
    if (name === "all" && checked) {
      setFilterState({
        all: true,
        vodomery: false,
        pausaly: false,
        srazkovaVoda: false,
      });
    }
    // Pokud uživatel odškrtnul "Vše", tak zrušíme všechny možnosti
    else if (name === "all" && !checked) {
      setFilterState({
        all: false,
        vodomery: false,
        pausaly: false,
        srazkovaVoda: false,
      });
    }
    // Uživatel zaškrtl/odškrtl jinou možnost, tak vypneme "Vše"
    else {
      setFilterState((prev) => ({
        ...prev,
        [name]: checked,
        all: false,
      }));
    }
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const sortedPoints = useMemo(() => {
    const pts = [...filteredPoints];
    switch (sortOption) {
      case "omAsc":
        pts.sort((a, b) => a.cisloOm - b.cisloOm);
        break;
      case "omDesc":
        pts.sort((a, b) => b.cisloOm - a.cisloOm);
        break;
      case "houseAsc":
        pts.sort(compareHouseAsc);
        break;
      case "houseDesc":
        pts.sort(compareHouseDesc);
        break;
      default:
      // žádné řazení
    }
    return pts;
  }, [filteredPoints, sortOption]);

  const sortedMeters = useMemo(() => {
    const pts = [...filteredMeters];
    switch (sortOption) {
      case "omAsc":
        pts.sort((a, b) => a.cisloOm - b.cisloOm);
        break;
      case "omDesc":
        pts.sort((a, b) => b.cisloOm - a.cisloOm);
        break;
      case "houseAsc":
        pts.sort(compareHouseAsc);
        break;
      case "houseDesc":
        pts.sort(compareHouseDesc);
        break;
      case "lastOdecetAsc":
        pts.sort((a, b) => {
          if (!a.lastOdecet && !b.lastOdecet) return 0;
          if (!a.lastOdecet) return 1;
          if (!b.lastOdecet) return -1;
          return new Date(a.lastOdecet) - new Date(b.lastOdecet);
        });
        break;
      case "lastOdecetDesc":
        pts.sort((a, b) => {
          if (!a.lastOdecet && !b.lastOdecet) return 0;
          if (!a.lastOdecet) return 1;
          if (!b.lastOdecet) return -1;
          return new Date(b.lastOdecet) - new Date(a.lastOdecet);
        });
        break;
      default:
      // žádné řazení
    }
    return pts;
  }, [filteredMeters, sortOption]);

  const paginatedPoints = useMemo(() => {
    // slice podle aktuální stránky
    return sortedPoints.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [page, sortedPoints]);

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Načítání...
        </Typography>
      </Box>
    );
  }

  if (error) {
    throw new Response(error.message, { status: error.status });
  }

  // pomocné stavy zobrazení
  const isDetailMode = selectedCollectionPoint;
  const isListMode = !isDetailMode && !bulkMode;
  const isBulkMode = !isDetailMode && bulkMode;

  return (
    <Box sx={{ margin: "0 auto", textAlign: "center" }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Odběrná místa
      </Typography>

      {!isDetailMode && (
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={bulkMode}
                onChange={(e) => setBulkMode(e.target.checked)}
              />
            }
            label="Hromadné zadávání stavů"
          />
        </FormGroup>
      )}

      {!isDetailMode && collectionPoints.length > 10 && (
        <>
          {/* Text field pro hledání */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            flexWrap="wrap"
          >
            <TextField
              fullWidth
              label="Hledat"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ maxWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth sx={{ maxWidth: 250 }}>
              <InputLabel id="obecSelector">Obec</InputLabel>
              <Select
                labelId="obecSelector"
                value={selectedObec}
                label="Obec"
                onChange={(e) => setSelectedObec(e.target.value)}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {obce.map((obec) => (
                  <MenuItem key={obec} value={obec}>
                    {obec}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ maxWidth: 250 }}>
              <InputLabel id="sort-select-label">Seřadit podle</InputLabel>
              <Select
                labelId="sort-select-label"
                value={sortOption}
                label="Seřadit podle"
                onChange={(e) => {
                  setSortOption(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                <MenuItem value="omAsc">Číslo odběrného místa ↑</MenuItem>
                <MenuItem value="omDesc">Číslo odběrného místa ↓</MenuItem>
                <MenuItem value="houseAsc">Číslo popisné ↑</MenuItem>
                <MenuItem value="houseDesc">Číslo popisné ↓</MenuItem>
                {isBulkMode && [
                  <MenuItem key="asc" value="lastOdecetAsc">
                    Poslední odečet ↑
                  </MenuItem>,
                  <MenuItem key="desc" value="lastOdecetDesc">
                    Poslední odečet ↓
                  </MenuItem>,
                ]}
              </Select>
            </FormControl>
          </Box>
          {/* Radio group */}
          {isBulkMode && (
            <FormControl sx={{ display: "flex", alignItems: "start", mt: 2 }}>
              <FormLabel id="demo-row-radio-buttons-group-label">
                Zobrazit
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue="vse"
                value={radioFilter}
                onChange={handleRadioChange}
              >
                <FormControlLabel value="vse" control={<Radio />} label="Vše" />
                <FormControlLabel
                  value="nezadane"
                  control={<Radio />}
                  label="Nezadané"
                />
                <FormControlLabel
                  value="zadane"
                  control={<Radio />}
                  label="Již zadané"
                />
              </RadioGroup>
            </FormControl>
          )}
        </>
      )}

      {isListMode && collectionPoints.length < 10 && (
        <Typography variant="body2" sx={{ mt: 2, mb: 2, textAlign: "center" }}>
          Kliknutím na odběrné místo (OM) zobrazíte jeho podrobnosti.
        </Typography>
      )}

      {/* Filtry pro OM, jen v list módu */}
      {isListMode && (
        <FormGroup row sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterState.all}
                onChange={handleCheckboxChange}
                name="all"
              />
            }
            label="Vše"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterState.vodomery}
                onChange={handleCheckboxChange}
                name="vodomery"
              />
            }
            label="Vodoměry"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterState.pausaly}
                onChange={handleCheckboxChange}
                name="pausaly"
              />
            }
            label="Paušály"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterState.srazkovaVoda}
                onChange={handleCheckboxChange}
                name="srazkovaVoda"
              />
            }
            label="Srážková voda"
          />
        </FormGroup>
      )}

      {isDetailMode && (
        <DetailOdbernehoMista
          selectedCollectionPoint={selectedCollectionPoint}
          handleBack={handleBack}
          showOdberatelNazev={showOdberatelNazev}
          onRefreshData={refreshCollectionPointsData}
        />
      )}

      {isListMode && (
        <>
          <List>
            {paginatedPoints.map((cp) => (
              <ListItemButton
                key={cp.cisloOm}
                sx={(theme) => ({
                  /* rámeček – primární barvy z tématu fungují v obou režimech */
                  border: 1,
                  borderColor: "primary.main",
                  borderRadius: 2,

                  /* výchozí pozadí průhledné (ať prosvítá background.default) */
                  backgroundColor: "transparent",

                  /* hover barva – jiná ve světlém a tmavém režimu */
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "#f0f8ff" // jemně modrá pro světlý motiv
                        : theme.palette.action.hover, // defaultní „hover“ pro tmavý
                    borderColor: "primary.dark",
                  },

                  mb: 1,
                })}
                onClick={() => handleViewDetail(cp.cisloOm)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <HomeWorkOutlinedIcon color="primary" fontSize="large" />
                    <ListItemText
                      primary={`OM č.: ${cp.cisloOm}`}
                      secondary={
                        <>
                          {showOdberatelNazev && cp.odberatelNazev != null && (
                            <>
                              Odběratel: {cp.odberatelNazev}
                              <br />
                            </>
                          )}
                          Číslo obce: {cp.cisloObce}
                          {", "} {cp.ulice}, {cp.obec}
                        </>
                      }
                      sx={{ marginLeft: "15px" }}
                    />
                  </Box>

                  {/* Pravá část: ikonky s počty */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      pr: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      <WaterDropIcon color="primary" />
                      <Typography variant="body2">
                        {cp.vodomery.length}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      <AssessmentIcon color="primary" />
                      <Typography variant="body2">
                        {cp.pausaly.length}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                    >
                      <WavesIcon color="primary" />
                      <Typography variant="body2">
                        {cp.srazkovaVoda.length}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ListItemButton>
            ))}
          </List>

          {filteredPoints.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredPoints.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
            />
          )}
        </>
      )}

      {isBulkMode && (
        <Vodomery
          vodomery={sortedMeters}
          setAllMeters={setAllMeters}
          showOdberatelNazev={showOdberatelNazev}
          onRefreshData={refreshCollectionPointsData}
        />
      )}

      {!isBulkMode && filteredPoints.length === 0 && (
        <Box>
          <Typography variant="body1">
            Nenalezeno žádné odběrné místo.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
